import { memo, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { EnrichedText } from '../components/content/EnrichedText';
import { AskAiPanel, type AskMessage } from '../components/training/AskAiPanel';
import { trainingService, type Lesson, type ProgressRecord, type QuizQuestion } from '../services/training.service';
import { getVimeoEmbedUrl } from '../utils/video';

type LessonTab = 'description' | 'transcript' | 'quiz' | 'ask';

const VideoPlayer = memo(function VideoPlayer({
  lessonId,
  videoUrl,
  videoRef,
  onTimeUpdate,
}: {
  lessonId: string;
  videoUrl?: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onTimeUpdate?: (time: number) => void;
}) {
  if (!videoUrl) {
    return (
      <div className="flex aspect-video items-center justify-center rounded-2xl bg-surface-container">
        <div className="text-center">
          <span className="material-symbols-outlined mb-2 block text-5xl text-on-surface-variant">play_circle</span>
          <p className="text-sm text-on-surface-variant">No video available</p>
        </div>
      </div>
    );
  }

  const isVimeo = videoUrl.includes('vimeo.com');
  const isYoutube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');

  if (isVimeo) {
    const embedUrl = getVimeoEmbedUrl(videoUrl, { autoplay: '0', color: 'c0c1ff' });
    if (!embedUrl) return null;
    return (
      <iframe
        key={`vimeo:${lessonId}`}
        src={embedUrl}
        className="w-full aspect-video rounded-2xl"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }

  if (isYoutube) {
    const ytId =
      videoUrl.match(/youtube\.com\/watch\?v=([^&]+)/)?.[1] ??
      videoUrl.match(/youtu\.be\/([^?]+)/)?.[1];
    return (
      <iframe
        key={`youtube:${lessonId}`}
        src={`https://www.youtube.com/embed/${ytId}`}
        className="w-full aspect-video rounded-2xl"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <video
      key={`file:${lessonId}`}
      ref={videoRef}
      src={videoUrl}
      controls
      className="w-full aspect-video rounded-2xl bg-surface-container"
      onTimeUpdate={(e) => onTimeUpdate?.(Math.floor(e.currentTarget.currentTime))}
    />
  );
}, (prev, next) => prev.lessonId === next.lessonId && prev.videoUrl === next.videoUrl);

function TranscriptPanel({
  timeline,
  onSeek,
}: {
  timeline: Array<{ startMs: number; endMs: number; startLabel: string; text: string }> | null;
  onSeek?: (ms: number) => void;
}) {
  if (!timeline || timeline.length === 0) {
    return <p className="py-4 text-center text-sm text-on-surface-variant">No transcript available.</p>;
  }

  return (
    <div className="max-h-[420px] space-y-1 overflow-y-auto">
      {timeline.map((entry, i) => (
        <button
          key={i}
          onClick={() => onSeek?.(entry.startMs)}
          className="flex w-full gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-surface-container"
        >
          <span className="shrink-0 font-mono text-xs text-primary">{entry.startLabel}</span>
          <span className="text-sm text-on-surface-variant">{entry.text}</span>
        </button>
      ))}
    </div>
  );
}

type QuizState = 'intro' | 'playing' | 'feedback' | 'result';

function QuizPanel({
  quiz,
  progress,
  onSubmit,
  submitting,
}: {
  quiz: QuizQuestion[];
  progress: ProgressRecord | null;
  onSubmit: (answers: number[]) => void;
  submitting: boolean;
}) {
  const [state, setState] = useState<QuizState>(progress?.quizPassed ? 'result' : 'intro');
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<number[]>(new Array(quiz.length).fill(-1));
  const [lastCorrect, setLastCorrect] = useState<boolean | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const total = quiz.length;

  useEffect(() => {
    setState(progress?.quizPassed ? 'result' : 'intro');
    setCurrent(0);
    setAnswers(new Array(quiz.length).fill(-1));
    setLastCorrect(null);
    setShowConfetti(false);
  }, [quiz.map((q) => q.question).join('|'), progress?.quizPassed]);

  const q = quiz[current];
  const progressPct = ((current + (state === 'result' ? 1 : 0)) / total) * 100;
  const localCorrect = answers.reduce((acc, a, i) => acc + (a === quiz[i].correctIndex ? 1 : 0), 0);
  const persistedScorePct = progress?.quizScore ?? null;
  const persistedCorrect = persistedScorePct != null ? Math.round((persistedScorePct / 100) * total) : null;
  const score = state === 'result' && progress?.quizPassed && persistedCorrect != null ? persistedCorrect : localCorrect;
  const passed = progress?.quizPassed || (state === 'result' && Math.round((localCorrect / total) * 100) >= 70);

  useEffect(() => {
    if (state === 'result' && passed) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 2500);
      return () => clearTimeout(t);
    }
  }, [state, passed]);

  function handleOption(oi: number) {
    if (state !== 'playing') return;
    const next = [...answers];
    next[current] = oi;
    setAnswers(next);
    setLastCorrect(oi === q.correctIndex);
    setState('feedback');
  }

  function handleNext() {
    if (current < total - 1) {
      setCurrent((c) => c + 1);
      setState('playing');
      setLastCorrect(null);
    } else {
      setState('result');
      onSubmit(answers);
    }
  }

  function handleRetry() {
    setCurrent(0);
    setAnswers(new Array(quiz.length).fill(-1));
    setLastCorrect(null);
    setState('playing');
  }

  if (state === 'intro') {
    return (
      <div className="rounded-2xl bg-surface-container-low p-6 text-center md:p-8">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <span className="material-symbols-outlined text-3xl">quiz</span>
        </div>
        <h3 className="mb-2 text-xl font-bold text-on-surface">Ready to test your knowledge?</h3>
        <p className="mx-auto mb-6 max-w-md text-sm text-on-surface-variant">
          Answer {total} questions. You need 70% to pass and unlock lesson completion.
        </p>
        <button
          onClick={() => setState('playing')}
          className="rounded-xl px-8 py-3 text-sm font-bold text-on-primary shadow-lg shadow-primary/15 transition-all hover:-translate-y-0.5 hover:shadow-primary/25 primary-gradient"
        >
          Start Quiz
        </button>
      </div>
    );
  }

  if (state === 'result') {
    const pct = progress?.quizPassed && persistedScorePct != null
      ? persistedScorePct
      : Math.round((score / total) * 100);
    return (
      <div className="relative overflow-hidden rounded-2xl bg-surface-container-low p-6 text-center md:p-8">
        {showConfetti ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              className="absolute inset-0 opacity-20"
              style={{ background: 'radial-gradient(circle at 50% 50%, var(--color-primary) 0%, transparent 70%)', animation: 'pulse 1.2s ease-in-out 2' }}
            />
          </div>
        ) : null}
        <div className={`mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full ${passed ? 'bg-success/15 text-success' : 'bg-error/15 text-error'}`}>
          <span className="material-symbols-outlined text-4xl">{passed ? 'check_circle' : 'cancel'}</span>
        </div>
        <h3 className="mb-1 text-2xl font-bold text-on-surface">{passed ? 'Great job!' : 'Keep learning!'}</h3>
        <p className="mb-4 text-sm text-on-surface-variant">
          You got <span className="font-bold text-on-surface">{score}/{total}</span> correct ({pct}%)
        </p>
        {passed ? (
          <p className="flex items-center justify-center gap-1 text-xs font-semibold text-success">
            <span className="material-symbols-outlined text-[16px]">lock_open</span>
            Lesson completion unlocked
          </p>
        ) : (
          <button
            onClick={handleRetry}
            className="rounded-xl bg-surface-container px-8 py-3 text-sm font-bold text-on-surface transition-colors hover:bg-surface-container-high"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5 rounded-2xl bg-surface-container-low p-5 md:p-6">
      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-container">
          <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="text-xs font-bold text-on-surface-variant">{current + 1}/{total}</span>
      </div>

      <div className="space-y-4">
        <h4 className="text-lg font-bold leading-snug text-on-surface md:text-xl">{q.question}</h4>
        <div className="grid grid-cols-1 gap-2.5">
          {q.options.map((opt, oi) => {
            const selected = answers[current] === oi;
            const isCorrect = q.correctIndex === oi;
            const showResult = state === 'feedback';
            const base = 'w-full text-left px-4 py-3.5 rounded-xl text-sm font-medium border-2 transition-all flex items-center gap-3';
            const style = showResult
              ? isCorrect
                ? 'bg-success/10 border-success text-success'
                : selected
                  ? 'bg-error/10 border-error text-error'
                  : 'bg-surface-container border-outline-variant/20 text-on-surface-variant opacity-60'
              : selected
                ? 'bg-primary/10 border-primary text-primary'
                : 'bg-surface-container border-outline-variant/20 text-on-surface hover:border-primary/40 hover:bg-surface-container-high';
            return (
              <button key={oi} disabled={showResult} onClick={() => handleOption(oi)} className={`${base} ${style}`}>
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  showResult
                    ? isCorrect
                      ? 'bg-success text-on-primary'
                      : selected
                        ? 'bg-error text-on-primary'
                        : 'bg-surface-container-high text-on-surface-variant'
                    : selected
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-container-high text-on-surface-variant'
                }`}>
                  {String.fromCharCode(65 + oi)}
                </span>
                <span className="flex-1">{opt}</span>
                {showResult && isCorrect ? <span className="material-symbols-outlined text-success">check_circle</span> : null}
                {showResult && selected && !isCorrect ? <span className="material-symbols-outlined text-error">cancel</span> : null}
              </button>
            );
          })}
        </div>
      </div>

      {state === 'feedback' ? (
        <div className={`rounded-xl border p-4 ${lastCorrect ? 'border-success/20 bg-success/10' : 'border-error/20 bg-error/10'}`}>
          <p className={`flex items-center gap-2 text-sm font-bold ${lastCorrect ? 'text-success' : 'text-error'}`}>
            <span className="material-symbols-outlined">{lastCorrect ? 'check_circle' : 'cancel'}</span>
            {lastCorrect ? 'Correct!' : 'Not quite'}
          </p>
          <p className="mt-1 text-xs text-on-surface-variant">
            {lastCorrect ? 'Nice work. Keep it up!' : `The correct answer was: ${q.options[q.correctIndex]}`}
          </p>
          <button
            onClick={handleNext}
            disabled={submitting}
            className="mt-3 rounded-xl px-6 py-2.5 text-sm font-bold text-on-primary shadow-lg shadow-primary/15 transition-all hover:-translate-y-0.5 hover:shadow-primary/25 disabled:opacity-50 primary-gradient"
          >
            {current < total - 1 ? 'Next Question' : 'Finish Quiz'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function LessonSidebar({
  title,
  lessons,
  completedIds,
  progressByLessonId,
  activeLessonId,
  collapsed,
  onToggleCollapsed,
  onSelect,
  onToggleComplete,
  canToggleComplete,
}: {
  title: string;
  lessons: Lesson[];
  completedIds: Set<string>;
  progressByLessonId: Map<string, ProgressRecord>;
  activeLessonId?: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onSelect: (lesson: Lesson) => void;
  onToggleComplete: (lesson: Lesson) => void;
  canToggleComplete: (lesson: Lesson) => boolean;
}) {
  if (collapsed) {
    return (
      <aside className="flex items-start justify-center">
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="rounded-2xl border border-outline-variant/10 bg-surface-container-low p-3 text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
          title="Expand chapters"
        >
          <span className="material-symbols-outlined text-[18px]">right_panel_open</span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="rounded-2xl border border-outline-variant/10 bg-surface-container-low p-3 md:p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="truncate pl-1 text-sm font-bold text-on-surface">{title}</h3>
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="rounded-lg p-2 text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
          title="Collapse chapters"
        >
          <span className="material-symbols-outlined text-[18px]">right_panel_close</span>
        </button>
      </div>

      <div className="space-y-1">
        {lessons.map((lesson) => {
          const done = completedIds.has(lesson.id);
          const active = lesson.id === activeLessonId;
          const quizPassed = progressByLessonId.get(lesson.id)?.quizPassed;
          const toggleAllowed = canToggleComplete(lesson);
          const toggleTitle = !done && !toggleAllowed
            ? 'Pass the quiz before marking this lesson as complete'
            : undefined;

          return (
            <div
              key={lesson.id}
              className={`flex items-center gap-3 rounded-xl p-2 transition-colors ${active ? 'bg-surface-container-high' : 'hover:bg-surface-container'}`}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!toggleAllowed) return;
                  onToggleComplete(lesson);
                }}
                disabled={!toggleAllowed}
                title={toggleTitle}
                className="shrink-0 disabled:cursor-not-allowed"
              >
                <div className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors ${
                  done
                    ? 'border-primary bg-primary'
                    : toggleAllowed
                      ? 'border-outline-variant hover:border-primary'
                      : 'border-outline-variant/40 bg-surface-container text-on-surface-variant/30'
                }`}>
                  {done ? <span className="material-symbols-outlined text-[14px] text-background">check</span> : null}
                </div>
              </button>

              <button
                type="button"
                onClick={() => onSelect(lesson)}
                title={lesson.title}
                className="min-w-0 flex-1 text-left"
              >
                <p className={`truncate text-sm font-medium ${done ? 'text-on-surface-variant line-through' : 'text-on-surface'}`}>
                  {lesson.order + 1}. {lesson.title}
                </p>
                <div className="flex items-center gap-2">
                  {lesson.duration > 0 ? <p className="text-xs text-on-surface-variant">{formatDuration(lesson.duration)}</p> : null}
                  {quizPassed ? <span className="text-[10px] font-semibold text-success">Quiz passed</span> : null}
                </div>
              </button>

              {active ? <span className="material-symbols-outlined shrink-0 text-sm text-primary">play_arrow</span> : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

export default function TrainingModulePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { trainingId, lessonId } = useParams<{ trainingId: string; lessonId?: string }>();
  const [lessonTab, setLessonTab] = useState<LessonTab>('ask');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [lessonChats, setLessonChats] = useState<Map<string, AskMessage[]>>(new Map());
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastWatchProgressSentRef = useRef(0);

  const { data: progress } = useQuery({
    queryKey: ['training-progress'],
    queryFn: () => trainingService.getProgress(),
    initialData: { total: 0, completed: 0, percentage: 0, records: [] },
  });

  const { data: trainingDetail, isLoading } = useQuery({
    queryKey: ['training', trainingId],
    queryFn: () => trainingService.findOne(trainingId!),
    enabled: !!trainingId,
  });

  const { data: lessonDetail } = useQuery({
    queryKey: ['lesson', trainingId, lessonId],
    queryFn: () => trainingService.getLesson(trainingId!, lessonId!),
    enabled: !!trainingId && !!lessonId,
  });

  const completedIds = new Set(progress.records.filter((r) => r.completed).map((r) => r.lessonId));
  const progressByLessonId = new Map(progress.records.map((record) => [record.lessonId, record] as const));
  const lessons: Lesson[] = (trainingDetail?.lessons as Lesson[] | undefined) ?? [];
  const activeLesson = lessons.find((lesson) => lesson.id === lessonId) ?? null;

  useEffect(() => {
    if (!trainingDetail || lessonId || trainingDetail.lessons.length === 0) return;
    navigate(`/training/modules/${trainingDetail.id}/lessons/${trainingDetail.lessons[0].id}`, { replace: true });
  }, [trainingDetail, lessonId, navigate]);

  useEffect(() => {
    if (lessonTab === 'ask') return;
    if (lessonDetail?.description) {
      setLessonTab('description');
      return;
    }
    if (lessonDetail?.transcript) {
      setLessonTab('transcript');
      return;
    }
    if (lessonDetail?.quiz && lessonDetail.quiz.length > 0) {
      setLessonTab('quiz');
      return;
    }
    setLessonTab('description');
  }, [lessonId, lessonDetail?.description, lessonDetail?.transcript, lessonDetail?.quiz]);

  useEffect(() => {
    lastWatchProgressSentRef.current = 0;
  }, [lessonId]);

  const toggleMutation = useMutation({
    mutationFn: ({ targetLessonId, completed }: { targetLessonId: string; completed: boolean }) =>
      trainingService.markProgress(targetLessonId, completed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-progress'] });
    },
    onError: (err: Error) => {
      alert(err.message);
    },
  });

  const quizMutation = useMutation({
    mutationFn: ({ answers }: { answers: number[] }) => trainingService.submitQuiz(trainingId!, lessonId!, answers),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-progress'] });
      queryClient.invalidateQueries({ queryKey: ['lesson', trainingId, lessonId] });
    },
  });

  function handleToggle(lesson: Lesson) {
    toggleMutation.mutate({
      targetLessonId: lesson.id,
      completed: !completedIds.has(lesson.id),
    });
  }

  function canToggleComplete(lesson: Lesson) {
    if (completedIds.has(lesson.id)) return true;
    const lessonHasQuiz = lesson.id === lessonDetail?.id
      ? Boolean(lessonDetail.quiz && lessonDetail.quiz.length > 0)
      : Boolean(lesson.quiz && lesson.quiz.length > 0);
    if (!lessonHasQuiz) return true;
    return Boolean(progressByLessonId.get(lesson.id)?.quizPassed);
  }

  function handleSeek(ms: number) {
    if (videoRef.current) {
      videoRef.current.currentTime = ms / 1000;
    }
  }

  if (isLoading || !trainingDetail) {
    return (
      <div className="flex min-h-[calc(100svh-3.5rem)] items-center justify-center md:min-h-screen">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex-1 max-w-7xl space-y-5 overflow-y-auto p-4 md:space-y-6 md:p-8 lg:p-12">
      <button
        type="button"
        onClick={() => navigate('/training')}
        className="flex items-center gap-2 text-sm text-on-surface-variant transition-colors hover:text-on-surface"
      >
        <span className="material-symbols-outlined text-sm">arrow_back</span>
        Back to modules
      </button>

      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Video Module</p>
          <h1 className="text-2xl font-bold text-on-surface md:text-3xl">{trainingDetail.title}</h1>
          {activeLesson ? <p className="mt-1 text-sm text-on-surface-variant">{activeLesson.title}</p> : null}
        </div>
        <div className="text-left md:text-right">
          <p className="text-sm font-semibold text-on-surface">
            {Array.from(completedIds).filter((id) => lessons.some((lesson) => lesson.id === id)).length}/{lessons.length} completed
          </p>
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-6 ${sidebarCollapsed ? 'lg:grid-cols-[minmax(0,1fr)_56px]' : 'lg:grid-cols-[minmax(0,1fr)_320px]'}`}>
        <div className="space-y-4">
          {activeLesson ? (
            <>
              <VideoPlayer
                lessonId={activeLesson.id}
                videoUrl={(lessonDetail ?? activeLesson).videoUrl}
                videoRef={videoRef}
                onTimeUpdate={(time) => {
                  if (trainingId && lessonId && time - lastWatchProgressSentRef.current >= 5) {
                    lastWatchProgressSentRef.current = time;
                    trainingService.updateWatchProgress(trainingId, lessonId, time).catch(() => null);
                  }
                }}
              />

              {(lessonDetail?.description || lessonDetail?.transcript || lessonDetail?.quiz || activeLesson) ? (
                <div className="flex w-full items-center gap-1 rounded-2xl border border-outline-variant/10 bg-surface-container-low p-1 md:w-fit flex-wrap">
                  {(['description', 'transcript', 'quiz', 'ask'] as LessonTab[]).map((tab) => {
                    const hasContent =
                      tab === 'description' ? !!lessonDetail?.description :
                      tab === 'transcript' ? !!lessonDetail?.transcript :
                      tab === 'quiz' ? !!(lessonDetail?.quiz && lessonDetail.quiz.length > 0) :
                      tab === 'ask' ? true :
                      false;
                    if (!hasContent) return null;
                    const isAsk = tab === 'ask';

                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setLessonTab(tab)}
                        className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                          lessonTab === tab
                            ? isAsk
                              ? 'bg-primary/15 text-primary shadow-sm'
                              : 'bg-surface-container-high text-on-surface shadow-sm'
                            : isAsk
                              ? 'text-primary hover:bg-primary/10'
                              : 'text-on-surface-variant hover:text-on-surface'
                        }`}
                      >
                        {isAsk && <span className="material-symbols-outlined text-[14px]">auto_awesome</span>}
                        {tab === 'description' ? 'Overview' : tab === 'transcript' ? 'Transcript' : tab === 'quiz' ? 'Quiz' : isAsk ? 'Ask AI' : tab}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {lessonTab === 'description' && lessonDetail?.description ? (
                <div className="rounded-2xl bg-surface-container-low p-5">
                  <h4 className="mb-3 text-sm font-bold text-on-surface">What you will learn</h4>
                  <EnrichedText content={lessonDetail.description} />
                </div>
              ) : null}

              {lessonTab === 'transcript' && lessonDetail?.transcriptTimeline ? (
                <div className="rounded-2xl bg-surface-container-low p-4">
                  <TranscriptPanel timeline={lessonDetail.transcriptTimeline} onSeek={handleSeek} />
                </div>
              ) : null}

              {lessonTab === 'quiz' && lessonDetail?.quiz && lessonDetail.quiz.length > 0 ? (
                <QuizPanel
                  quiz={lessonDetail.quiz}
                  progress={lessonDetail.progress ?? null}
                  onSubmit={(answers) => quizMutation.mutate({ answers })}
                  submitting={quizMutation.isPending}
                />
              ) : null}

              {lessonTab === 'ask' && activeLesson && (
                <AskAiPanel
                  lessonId={activeLesson.id}
                  onNavigateToVideo={(tid, lid) => navigate(`/training/modules/${tid}/lessons/${lid}`)}
                  messages={lessonChats.get(activeLesson.id) ?? []}
                  onMessagesChange={(msgs) =>
                    setLessonChats((prev) => new Map(prev).set(activeLesson.id, msgs))
                  }
                />
              )}

              {lessonDetail?.quiz && lessonDetail.quiz.length > 0 && !lessonDetail.progress?.quizPassed ? (
                <div className="flex items-center gap-2 text-sm text-on-surface-variant">
                  <span className="material-symbols-outlined text-tertiary">lock</span>
                  Pass the quiz to mark this lesson as complete
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => handleToggle(activeLesson)}
                  disabled={toggleMutation.isPending}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                    completedIds.has(activeLesson.id)
                      ? 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container'
                      : 'text-on-primary primary-gradient'
                  }`}
                >
                  {completedIds.has(activeLesson.id) ? 'Mark as incomplete' : 'Mark as complete'}
                </button>
              )}
            </>
          ) : (
            <div className="flex aspect-video items-center justify-center rounded-2xl bg-surface-container-low">
              <div className="text-center">
                <span className="material-symbols-outlined mb-3 block text-5xl text-on-surface-variant">play_circle</span>
                <p className="font-medium text-on-surface">Select a lesson to start</p>
              </div>
            </div>
          )}
        </div>

        <LessonSidebar
          title={trainingDetail.title}
          lessons={lessons}
          completedIds={completedIds}
          progressByLessonId={progressByLessonId}
          activeLessonId={activeLesson?.id}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
          onSelect={(lesson) => navigate(`/training/modules/${trainingDetail.id}/lessons/${lesson.id}`)}
          onToggleComplete={handleToggle}
          canToggleComplete={canToggleComplete}
        />
      </div>
    </div>
  );
}
